<template>
    <modal @close="$emit('close')" ref="modal" size="xl">
        <template v-slot:title>
            POS Log — {{ client.name }}
            <span v-if="task"> / Task {{ task.id }}</span>
        </template>

        <slot>
            <div v-if="loading" class="text-center my-3">
                <i class="far fa-spinner fa-spin"></i> Loading…
            </div>

            <div v-if="!task && !loading">
                <p v-if="!tasks.length" class="text-muted">No POS imports or syncs have run for this client.</p>

                <div class="table-responsive" v-if="tasks.length">
                    <table class="table table-sm table-hover small text-nowrap mb-2">
                        <thead>
                            <tr>
                                <th>Task</th>
                                <th>Started</th>
                                <th>POS</th>
                                <th>Company</th>
                                <th>Period</th>
                                <th>Paygroup</th>
                                <th>Store</th>
                                <th>User</th>
                                <th class="text-center">Step</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="t in tasks" :key="t.id" class="pos-log-row" @click="openTask(t)">
                                <td><a href @click.prevent>{{ t.id }}</a></td>
                                <td>{{ $filters.datetimeshort(t.dt_started, 'MM/DD/YY h:mm a') }}</td>
                                <td>{{ posLabel(t) }}</td>
                                <td :title="t.entity_name">{{ companyLabel(t) }}</td>
                                <td><span v-if="t.date_start">{{ period(t) }}</span></td>
                                <td>{{ t.paygroup }}</td>
                                <td>{{ t.store }}</td>
                                <td class="pos-log-user" :title="t.user_email">{{ t.user_email }}</td>
                                <td class="text-center">{{ t.step }}</td>
                                <td><span class="badge" :class="statusClass(t)">{{ t.status_display }}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="d-flex align-items-center" v-if="numPages > 1">
                    <button type="button" class="btn btn-sm btn-outline-primary" :disabled="page <= 1" @click.prevent="loadPage(page - 1)">Newer</button>
                    <span class="mx-2 text-muted">Page {{ page }} of {{ numPages }} ({{ count }} tasks)</span>
                    <button type="button" class="btn btn-sm btn-outline-primary" :disabled="page >= numPages" @click.prevent="loadPage(page + 1)">Older</button>
                </div>
            </div>

            <div v-if="task && !loading">
                <p>
                    <a href @click.prevent="task = null"><i class="far fa-fw fa-chevron-left"></i> Back to log</a>
                </p>

                <div class="row">
                    <div class="col-sm-6">
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.title }}</span>
                            <label>Task</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.entity_code }} - {{ task.entity_name }}</span>
                            <label>{{ task.entity_type == 'company' ? 'Company' : 'Client' }}</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield">{{ posLabel(task) }}</span>
                            <label>POS</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.user_email }}</span>
                            <label>User</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.step }}</span>
                            <label>Step</label>
                        </div>
                    </div>
                    <div class="col-sm-6">
                        <div class="mui-textfield">
                            <span class="textfield">{{ $filters.datetimeshort(task.dt_started) }}</span>
                            <label>Started</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield"><span v-if="task.dt_complete">{{ $filters.datetimeshort(task.dt_complete) }}</span></span>
                            <label>Completed</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield"><span class="badge" :class="statusClass(task)">{{ task.status_display }}</span></span>
                            <label>Status</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-6">
                        <h5 class="mt-2 mb-2">Parameters</h5>
                        <div class="mui-textfield">
                            <span class="textfield"><span v-if="task.date_start">{{ period(task) }}</span></span>
                            <label>Period</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.paygroup }}</span>
                            <label>Paygroup</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.store }}</span>
                            <label>Store</label>
                        </div>
                    </div>
                    <div class="col-sm-6">
                        <h5 class="mt-2 mb-2">Totals</h5>
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.total_hours_reg }}</span>
                            <label>Regular Hours</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.total_hours_ot }}</span>
                            <label>OT Hours</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.total_hours_other }}</span>
                            <label>Other Hours</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.total_other_earnings }}</span>
                            <label>Other Earnings</label>
                        </div>
                        <div class="mui-textfield">
                            <span class="textfield">{{ task.total_employees_for_payroll }}</span>
                            <label>Employees</label>
                        </div>
                    </div>
                </div>

                <h5 class="mt-2 mb-2">Results</h5>

                <div class="alert alert-danger" v-if="task.error_display || task.error_detail">
                    <strong v-if="task.error_display">{{ task.error_display }}</strong>
                    <div v-if="task.error_detail">{{ task.error_detail }}</div>
                </div>

                <div v-for="m in messages" :key="m.key" class="alert" :class="m.severity == 'error' ? 'alert-danger' : 'alert-warning'">
                    <div class="d-flex align-items-start">
                        <strong class="flex-grow-1">{{ m.description }}</strong>
                        <a v-if="hasDetail(m)" href class="ml-2 text-nowrap" @click.prevent="toggle(m.key)">
                            {{ expanded[m.key] ? 'Hide Detail' : 'Show Detail' }}
                        </a>
                    </div>
                    <div v-if="expanded[m.key]" class="mt-2">
                        <pre v-if="m.detail" class="mb-0 small"><b v-if="m.detail_header">{{ m.detail_header }}</b>{{ m.detail }}</pre>
                        <div v-if="m.rows" class="table-responsive pos-log-detail">
                            <table class="table table-sm small text-nowrap mb-0">
                                <thead>
                                    <tr>
                                        <th v-for="(c, ci) in m.columns" :key="ci" :class="{ 'text-right': c.numeric }">{{ c.label }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, ri) in m.rows" :key="ri">
                                        <td v-for="(value, ci) in row" :key="ci" :class="{ 'text-right': m.columns[ci].numeric }">{{ cell(value, m.columns[ci]) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <p class="mb-1" v-if="task.raw_num_lines">
                    Original download: {{ task.raw_num_lines }} lines
                    <a v-if="task.downloads.includes('raw')" class="btn btn-outline-primary btn-sm ml-1" :href="downloadUrl('raw')">Download raw data</a>
                </p>
                <p class="mb-1" v-if="task.imported_num_lines">
                    File for import: {{ task.imported_num_lines }} lines
                    <a v-if="task.downloads.includes('imported')" class="btn btn-outline-primary btn-sm ml-1" :href="downloadUrl('imported')">Download imported file</a>
                </p>
                <p class="text-muted small" v-if="task.ssn_matched && task.downloads.length">
                    These files hold full SSNs, and each download is logged.
                </p>
            </div>
        </slot>

        <template v-slot:footer>
            <div class="form-row">
                <div class="col">
                    <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Close</button>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import ModalMixin from '@/mixins/ModalMixin'

// HoursImportTask.HOURS_IMPORT_STATUS_*
const STATUS_INPROGRESS = 10
const STATUS_ERROR = 30

const PERIOD_FORMAT = 'MM/DD/YY'

export default {
    mixins: [ModalMixin],
    props: ['client'],
    data() {
        return {
            loading: false,
            tasks: [],
            page: 1,
            numPages: 1,
            count: 0,
            task: null,
            expanded: {},
        }
    },
    computed: {
        messages() {
            if (!this.task) {
                return []
            }
            const tag = (severity) => (m, i) => ({ ...m, severity, key: `${severity}-${i}` })
            return [
                ...(this.task.errors || []).map(tag('error')),
                ...(this.task.warnings || []).map(tag('warning')),
            ]
        },
    },
    methods: {
        reset() {
            this.task = null
            this.tasks = []
            this.loadPage(1)
        },
        loadPage(page) {
            this.loading = true
            return this.$api.get(`/admin/clients/${this.client.id}/pos-log?page=${page}`).then((resp) => {
                this.tasks = resp.tasks
                this.page = resp.page
                this.numPages = resp.num_pages
                this.count = resp.count
                this.loading = false
            }).catch((errors) => {
                this.loading = false
                this.$bus.showError(this.$bus.firstError(errors))
            })
        },
        openTask(task) {
            this.loading = true
            return this.$api.get(`/admin/clients/${this.client.id}/pos-log/${task.id}`).then((resp) => {
                this.task = resp
                this.expanded = {}
                this.loading = false
            }).catch((errors) => {
                this.loading = false
                this.$bus.showError(this.$bus.firstError(errors))
            })
        },
        hasDetail(m) {
            return Boolean(m.detail || (m.rows && m.rows.length))
        },
        toggle(key) {
            this.expanded = { ...this.expanded, [key]: !this.expanded[key] }
        },
        cell(value, column) {
            if (column.numeric && value !== '' && value !== null && !isNaN(value)) {
                return Number(value).toFixed(2)
            }
            return value
        },
        downloadUrl(kind) {
            return `${this.$api.API_BASE}/admin/clients/${this.client.id}/pos-log/${this.task.id}/download/${kind}`
        },
        statusClass(task) {
            if (task.status == STATUS_ERROR) {
                return 'badge-danger'
            }
            if (task.status == STATUS_INPROGRESS) {
                return 'badge-info'
            }
            return 'badge-success'
        },
        companyLabel(task) {
            return task.entity_type == 'company' ? task.entity_code : 'All'
        },
        period(task) {
            return `${this.$filters.dateshort(task.date_start, PERIOD_FORMAT)} – ${this.$filters.dateshort(task.date_end, PERIOD_FORMAT)}`
        },
        posLabel(task) {
            if (task.importer_code == 'employee_sync') {
                return 'Employee Sync'
            }
            return (task.importer_code || '').toUpperCase()
        },
    },
}
</script>

<style scoped>
    .pos-log-row {
        cursor: pointer;
    }

    .pos-log-detail {
        max-height: 24rem;
        overflow-y: auto;
    }

    .pos-log-user {
        max-width: 16rem;
        overflow: hidden;
        text-overflow: ellipsis;
    }
</style>
